import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Grid,
  Avatar,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardContent
} from '@mui/material';
import {
  Person,
  Phone,
  LocationOn,
  Email,
  Work,
  School,
  Save,
  GitHub,
  LinkedIn,
  Upload,
  Delete,
  Description,
  AttachFile,
  Link,
  Edit,
  Cancel,
  Download
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  
  // Language state
  const [languages, setLanguages] = useState([]);
  const [newLanguage, setNewLanguage] = useState({
    name: '',
    canRead: false,
    canWrite: false,
    canSpeak: false
  });
  const [showOtherLanguage, setShowOtherLanguage] = useState(false);
  const [customLanguage, setCustomLanguage] = useState('');
  
  // Documents state
  const [uploadedDocuments, setUploadedDocuments] = useState({
    resume: null,
    coverLetter: null,
    certificates: [],
    portfolioSamples: []
  });
  
  // File input refs
  const resumeInputRef = React.useRef(null);
  const coverLetterInputRef = React.useRef(null);
  const certificatesInputRef = React.useRef(null);
  const portfolioInputRef = React.useRef(null);
  const profilePhotoRef = React.useRef(null);

  const [formData, setFormData] = useState({
    profile: {
      firstName: '',
      lastName: '',
      phone: '',
      dateOfBirth: '',
      address: {
        city: '',
        state: '',
        street: '',
        zipCode: '',
        country: ''
      },
      bio: '',
      education: '',
      experience: 'Fresher',
      skills: [],
      socialLinks: {
        linkedin: '',
        github: '',
        portfolio: ''
      },
      languages: []
    }
  });

  useEffect(() => {
    if (user?.profile) {
      const profileData = {
        profile: {
          firstName: user.profile.firstName || '',
          lastName: user.profile.lastName || '',
          phone: user.profile.phone || '',
          dateOfBirth: user.profile.dateOfBirth || '',
          address: {
            city: user.profile.address?.city || '',
            state: user.profile.address?.state || '',
            street: user.profile.address?.street || '',
            zipCode: user.profile.address?.zipCode || '',
            country: user.profile.address?.country || ''
          },
          bio: user.profile.bio || '',
          education: user.profile.education || '',
          experience: user.profile.experience || 'Fresher',
          skills: user.profile.skills || [],
          socialLinks: {
            linkedin: user.profile.socialLinks?.linkedin || '',
            github: user.profile.socialLinks?.github || '',
            portfolio: user.profile.socialLinks?.portfolio || ''
          },
          languages: user.profile.languages || []
        }
      };
      setFormData(profileData);
      setLanguages(user.profile.languages || []);
      
      // Load saved documents - Check multiple possible locations
      const documents = user.profile.documents || user.documents || {};
      console.log('Loading documents from user:', documents);
      
      setUploadedDocuments({
        resume: documents.resume || null,
        coverLetter: documents.coverLetter || null,
        certificates: Array.isArray(documents.certificates) ? documents.certificates : [],
        portfolioSamples: Array.isArray(documents.portfolioSamples) ? documents.portfolioSamples : []
      });
      
      // Load profile photo
      const avatar = user.profile.avatar || user.avatar;
      if (avatar) {
        setProfilePhoto(avatar);
      }
      
      // Check if profile is complete to determine initial edit mode
      const isComplete = user.profile.firstName && 
                        user.profile.lastName && 
                        user.profile.phone &&
                        user.profile.address?.city &&
                        user.profile.address?.state;
      setEditMode(!isComplete);
    } else {
      setEditMode(true);
    }
  }, [user]);

  const experienceLevels = [
    'Fresher',
    '0-2 Years',
    '2-5 Years',
    '5-10 Years',
    '10+ Years'
  ];

  const commonLanguages = ['English', 'Hindi', 'Odia', 'Others'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const keys = name.split('.');
      setFormData(prev => {
        const newData = { ...prev };
        let current = newData;
        
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }
        
        current[keys[keys.length - 1]] = value;
        return newData;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleAddLanguage = () => {
    const languageName = showOtherLanguage ? customLanguage : newLanguage.name;
    
    if (languageName && (newLanguage.canRead || newLanguage.canWrite || newLanguage.canSpeak)) {
      const newLang = {
        name: languageName,
        canRead: newLanguage.canRead,
        canWrite: newLanguage.canWrite,
        canSpeak: newLanguage.canSpeak
      };
      
      setLanguages([...languages, newLang]);
      setNewLanguage({
        name: '',
        canRead: false,
        canWrite: false,
        canSpeak: false
      });
      setCustomLanguage('');
      setShowOtherLanguage(false);
    }
  };

  const handleRemoveLanguage = (index) => {
    setLanguages(languages.filter((_, i) => i !== index));
  };

  const handleLanguageSelect = (e) => {
    const value = e.target.value;
    if (value === 'Others') {
      setShowOtherLanguage(true);
      setNewLanguage({ ...newLanguage, name: '' });
    } else {
      setShowOtherLanguage(false);
      setNewLanguage({ ...newLanguage, name: value });
    }
  };

  // File upload handlers
  const handleFileUpload = (event, documentType) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (documentType === 'profilePhoto') {
      // Handle profile photo
      const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedImageTypes.includes(file.type)) {
        setError('Please upload JPEG, PNG, or GIF image files only');
        return;
      }
      
      // Create a preview URL for the image
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhoto(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      // Validate document file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload PDF, DOC, or DOCX files only');
        return;
      }
      
      // Update state based on document type
      if (documentType === 'resume') {
        setUploadedDocuments({...uploadedDocuments, resume: file.name});
      } else if (documentType === 'coverLetter') {
        setUploadedDocuments({...uploadedDocuments, coverLetter: file.name});
      } else if (documentType === 'certificates') {
        setUploadedDocuments({
          ...uploadedDocuments,
          certificates: [...uploadedDocuments.certificates, file.name]
        });
      } else if (documentType === 'portfolio') {
        setUploadedDocuments({
          ...uploadedDocuments,
          portfolioSamples: [...uploadedDocuments.portfolioSamples, file.name]
        });
      }
    }
    
    setError(''); // Clear any previous errors
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields and documents
    if (!uploadedDocuments.resume) {
      setError('Please upload your resume before saving the profile');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Process skills
      const skillsArray = typeof formData.profile.skills === 'string' 
        ? formData.profile.skills.split(',').map(skill => skill.trim()).filter(Boolean)
        : formData.profile.skills;

      const profileData = {
        ...formData.profile,
        skills: skillsArray,
        languages: languages,
        avatar: profilePhoto,
        documents: {
          resume: uploadedDocuments.resume,
          coverLetter: uploadedDocuments.coverLetter,
          certificates: uploadedDocuments.certificates,
          portfolioSamples: uploadedDocuments.portfolioSamples
        }
      };
      
      console.log('Saving profile with documents:', profileData.documents);
      
      const result = await updateProfile(profileData);
      
      if (result.success) {
        setSuccess('Profile updated successfully!');
        
        // Update local state with saved data
        if (result.user) {
          const docs = result.user.profile?.documents || {};
          setUploadedDocuments({
            resume: docs.resume || uploadedDocuments.resume,
            coverLetter: docs.coverLetter || uploadedDocuments.coverLetter,
            certificates: docs.certificates || uploadedDocuments.certificates,
            portfolioSamples: docs.portfolioSamples || uploadedDocuments.portfolioSamples
          });
        }
        
        setEditMode(false);
        // Don't reload immediately - let the user see the success message
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      } else {
        setError(result.message || 'Failed to update profile');
      }
    } catch (err) {
      setError('An error occurred while updating profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to user's current profile
    if (user?.profile) {
      setFormData({
        profile: {
          firstName: user.profile.firstName || '',
          lastName: user.profile.lastName || '',
          phone: user.profile.phone || '',
          dateOfBirth: user.profile.dateOfBirth || '',
          address: {
            city: user.profile.address?.city || '',
            state: user.profile.address?.state || '',
            street: user.profile.address?.street || '',
            zipCode: user.profile.address?.zipCode || '',
            country: user.profile.address?.country || ''
          },
          bio: user.profile.bio || '',
          education: user.profile.education || '',
          experience: user.profile.experience || 'Fresher',
          skills: user.profile.skills || [],
          socialLinks: {
            linkedin: user.profile.socialLinks?.linkedin || '',
            github: user.profile.socialLinks?.github || '',
            portfolio: user.profile.socialLinks?.portfolio || ''
          },
          languages: user.profile.languages || []
        }
      });
      setLanguages(user.profile.languages || []);
      // Reset uploaded documents to saved state
      if (user.profile.documents) {
        setUploadedDocuments({
          resume: user.profile.documents.resume || null,
          coverLetter: user.profile.documents.coverLetter || null,
          certificates: user.profile.documents.certificates || [],
          portfolioSamples: user.profile.documents.portfolioSamples || []
        });
      }
      if (user.profile.avatar) {
        setProfilePhoto(user.profile.avatar);
      }
    }
    setEditMode(false);
  };

  const isProfileComplete = () => {
    return user?.profile?.firstName && 
           user?.profile?.lastName && 
           user?.profile?.phone &&
           user?.profile?.address?.city &&
           user?.profile?.address?.state;
  };

  const formatLanguages = (langs) => {
    if (!langs || langs.length === 0) return 'Not specified';
    return langs.map(lang => {
      const abilities = [];
      if (lang.canRead) abilities.push('Read');
      if (lang.canWrite) abilities.push('Write');
      if (lang.canSpeak) abilities.push('Speak');
      return `${lang.name} (${abilities.join(', ')})`;
    }).join(', ');
  };

  // Function to simulate file download
  const handleDownloadDocument = (fileName, documentType) => {
    // Create a sample text content for the document
    const documentContent = `This is a sample ${documentType} document.\n\nFilename: ${fileName}\nUser: ${user?.email}\nGenerated: ${new Date().toLocaleString()}\n\n[This is a demo file. In production, this would contain the actual uploaded document content.]`;
    
    // Create a blob from the text
    const blob = new Blob([documentContent], { type: 'text/plain' });
    
    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);
    
    // Create a temporary anchor element and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || `${documentType.toLowerCase()}.txt`;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    // Show success message
    setSuccess(`Downloading ${fileName || documentType}...`);
    setTimeout(() => setSuccess(''), 2000);
  };

  // Read-only view
  const ProfileView = () => (
    <Box>
      <Grid container spacing={3}>
        {/* Profile Header with Photo */}
        <Grid item xs={12} sx={{ textAlign: 'center', mb: 2 }}>
          <Avatar
            sx={{ width: 120, height: 120, margin: '0 auto', mb: 2 }}
            src={profilePhoto}
          >
            {!profilePhoto && (formData.profile.firstName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase())}
          </Avatar>
          <Typography variant="h5">
            {formData.profile.firstName && formData.profile.lastName ? 
              `${formData.profile.firstName} ${formData.profile.lastName}` : 
              user?.email
            }
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
            <Chip 
              icon={<Email />} 
              label={user?.email} 
              size="small" 
              variant="outlined" 
            />
            <Chip 
              icon={<Work />} 
              label={user?.role} 
              size="small" 
              color="primary" 
            />
          </Box>
        </Grid>

        {/* Personal Information */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Personal Information</Typography>
                <Button
                  startIcon={<Edit />}
                  variant="contained"
                  onClick={() => setEditMode(true)}
                >
                  Edit Profile
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">First Name</Typography>
                  <Typography variant="body1">{formData.profile.firstName || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Last Name</Typography>
                  <Typography variant="body1">{formData.profile.lastName || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
                  <Typography variant="body1">{formData.profile.phone || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Date of Birth</Typography>
                  <Typography variant="body1">
                    {formData.profile.dateOfBirth ? new Date(formData.profile.dateOfBirth).toLocaleDateString() : 'Not provided'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">City</Typography>
                  <Typography variant="body1">{formData.profile.address.city || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">State</Typography>
                  <Typography variant="body1">{formData.profile.address.state || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Bio</Typography>
                  <Typography variant="body1">{formData.profile.bio || 'Not provided'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Professional Information */}
        {user?.role === 'candidate' && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Professional Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Education</Typography>
                    <Typography variant="body1">{formData.profile.education || 'Not provided'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Experience Level</Typography>
                    <Typography variant="body1">{formData.profile.experience || 'Fresher'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Skills</Typography>
                    <Typography variant="body1">
                      {Array.isArray(formData.profile.skills) && formData.profile.skills.length > 0 
                        ? formData.profile.skills.join(', ') 
                        : 'Not provided'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Languages</Typography>
                    <Typography variant="body1">{formatLanguages(languages)}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Social Links */}
        {user?.role === 'candidate' && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Social Links</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="textSecondary">LinkedIn</Typography>
                    <Typography variant="body1">
                      {formData.profile.socialLinks.linkedin || 'Not provided'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="textSecondary">GitHub</Typography>
                    <Typography variant="body1">
                      {formData.profile.socialLinks.github || 'Not provided'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="textSecondary">Portfolio</Typography>
                    <Typography variant="body1">
                      {formData.profile.socialLinks.portfolio || 'Not provided'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Documents */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Documents</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Description color={uploadedDocuments.resume ? "success" : "error"} />
                        <Box>
                          <Typography variant="subtitle1">
                            Resume <Chip label="Mandatory" size="small" color="error" />
                          </Typography>
                          <Typography variant="body2" color={uploadedDocuments.resume ? "success.main" : "textSecondary"}>
                            {uploadedDocuments.resume ? 
                              `✓ Uploaded: ${uploadedDocuments.resume}` : 
                              'No resume uploaded'
                            }
                          </Typography>
                        </Box>
                      </Box>
                      {uploadedDocuments.resume && (
                        <IconButton 
                          color="primary" 
                          onClick={() => handleDownloadDocument(uploadedDocuments.resume, 'Resume')}
                          title="Download Resume"
                        >
                          <Download />
                        </IconButton>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                {uploadedDocuments.coverLetter && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AttachFile color="success" />
                          <Box>
                            <Typography variant="subtitle1">Cover Letter</Typography>
                            <Typography variant="body2" color="success.main">
                              ✓ Uploaded: {uploadedDocuments.coverLetter}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton 
                          color="primary"
                          onClick={() => handleDownloadDocument(uploadedDocuments.coverLetter, 'Cover Letter')}
                          title="Download Cover Letter"
                        >
                          <Download />
                        </IconButton>
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {uploadedDocuments.certificates.length > 0 && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AttachFile color="success" />
                          <Box>
                            <Typography variant="subtitle1">Certificates</Typography>
                            <Typography variant="body2" color="success.main">
                              ✓ {uploadedDocuments.certificates.length} certificate(s) uploaded
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ mt: 1 }}>
                          {uploadedDocuments.certificates.map((cert, index) => (
                            <Chip 
                              key={index} 
                              label={cert} 
                              size="small" 
                              sx={{ mr: 1, mb: 1, cursor: 'pointer' }}
                              onClick={() => handleDownloadDocument(cert, 'Certificate')}
                              onDelete={() => handleDownloadDocument(cert, 'Certificate')}
                              deleteIcon={<Download />}
                              title={`Download ${cert}`}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {uploadedDocuments.portfolioSamples.length > 0 && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AttachFile color="success" />
                          <Box>
                            <Typography variant="subtitle1">Work Portfolio Samples</Typography>
                            <Typography variant="body2" color="success.main">
                              ✓ {uploadedDocuments.portfolioSamples.length} sample(s) uploaded
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ mt: 1 }}>
                          {uploadedDocuments.portfolioSamples.map((sample, index) => (
                            <Chip 
                              key={index} 
                              label={sample} 
                              size="small" 
                              sx={{ mr: 1, mb: 1, cursor: 'pointer' }}
                              onClick={() => handleDownloadDocument(sample, 'Portfolio Sample')}
                              onDelete={() => handleDownloadDocument(sample, 'Portfolio Sample')}
                              deleteIcon={<Download />}
                              title={`Download ${sample}`}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // Edit form view
  const ProfileForm = () => (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} sx={{ textAlign: 'center' }}>
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Avatar
              sx={{ width: 120, height: 120, margin: '0 auto', mb: 2 }}
              src={profilePhoto}
            >
              {!profilePhoto && (formData.profile.firstName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase())}
            </Avatar>
            <input
              type="file"
              ref={profilePhotoRef}
              style={{ display: 'none' }}
              accept="image/*"
              onChange={(e) => handleFileUpload(e, 'profilePhoto')}
            />
            <IconButton
              sx={{
                position: 'absolute',
                bottom: 10,
                right: -10,
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }}
              size="small"
              onClick={() => profilePhotoRef.current?.click()}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="h6">
            {formData.profile.firstName && formData.profile.lastName ? 
              `${formData.profile.firstName} ${formData.profile.lastName}` : 
              user?.email
            }
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
            <Chip 
              icon={<Email />} 
              label={user?.email} 
              size="small" 
              variant="outlined" 
            />
            <Chip 
              icon={<Work />} 
              label={user?.role} 
              size="small" 
              color="primary" 
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }}>Personal Information</Divider>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            name="profile.firstName"
            value={formData.profile.firstName}
            onChange={handleChange}
            required
            InputProps={{
              startAdornment: <Person sx={{ mr: 1, color: 'action.active' }} />
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            name="profile.lastName"
            value={formData.profile.lastName}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone"
            name="profile.phone"
            value={formData.profile.phone}
            onChange={handleChange}
            required
            InputProps={{
              startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} />
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Date of Birth"
            name="profile.dateOfBirth"
            type="date"
            value={formData.profile.dateOfBirth}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: new Date().toISOString().split('T')[0],
              min: '1900-01-01'
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }}>Address</Divider>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="City"
            name="profile.address.city"
            value={formData.profile.address.city}
            onChange={handleChange}
            required
            InputProps={{
              startAdornment: <LocationOn sx={{ mr: 1, color: 'action.active' }} />
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="State"
            name="profile.address.state"
            value={formData.profile.address.state}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Bio / About Me"
            name="profile.bio"
            value={formData.profile.bio}
            onChange={handleChange}
            placeholder="Tell us about yourself..."
          />
        </Grid>

        {user?.role === 'candidate' && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>Professional Information</Divider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Education"
                name="profile.education"
                value={formData.profile.education}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <School sx={{ mr: 1, color: 'action.active' }} />
                }}
                placeholder="e.g., B.Tech in Computer Science"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Experience Level</InputLabel>
                <Select
                  name="profile.experience"
                  value={formData.profile.experience}
                  onChange={handleChange}
                  label="Experience Level"
                >
                  {experienceLevels.map(level => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Skills (comma separated)"
                name="profile.skills"
                value={Array.isArray(formData.profile.skills) ? formData.profile.skills.join(', ') : formData.profile.skills}
                onChange={handleChange}
                placeholder="e.g., JavaScript, React, Node.js"
                helperText="Separate skills with commas"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>Languages</Divider>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    {!showOtherLanguage ? (
                      <FormControl fullWidth>
                        <InputLabel>Select Language</InputLabel>
                        <Select
                          value={newLanguage.name}
                          onChange={handleLanguageSelect}
                          label="Select Language"
                        >
                          <MenuItem value="">Select</MenuItem>
                          {commonLanguages.map(lang => (
                            <MenuItem key={lang} value={lang}>
                              {lang}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        fullWidth
                        label="Enter Language"
                        value={customLanguage}
                        onChange={(e) => setCustomLanguage(e.target.value)}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={newLanguage.canRead}
                            onChange={(e) => setNewLanguage({...newLanguage, canRead: e.target.checked})}
                          />
                        }
                        label="Read"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={newLanguage.canWrite}
                            onChange={(e) => setNewLanguage({...newLanguage, canWrite: e.target.checked})}
                          />
                        }
                        label="Write"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={newLanguage.canSpeak}
                            onChange={(e) => setNewLanguage({...newLanguage, canSpeak: e.target.checked})}
                          />
                        }
                        label="Speak"
                      />
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      variant="outlined"
                      onClick={handleAddLanguage}
                      fullWidth
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              
              {languages.length > 0 && (
                <List>
                  {languages.map((lang, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={lang.name}
                        secondary={`Can ${lang.canRead ? 'Read' : ''}${lang.canRead && lang.canWrite ? ', ' : ''}${lang.canWrite ? 'Write' : ''}${(lang.canRead || lang.canWrite) && lang.canSpeak ? ', ' : ''}${lang.canSpeak ? 'Speak' : ''}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" onClick={() => handleRemoveLanguage(index)}>
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>Social Links</Divider>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="LinkedIn Profile"
                name="profile.socialLinks.linkedin"
                value={formData.profile.socialLinks.linkedin}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/yourprofile"
                InputProps={{
                  startAdornment: <LinkedIn sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="GitHub Profile"
                name="profile.socialLinks.github"
                value={formData.profile.socialLinks.github}
                onChange={handleChange}
                placeholder="https://github.com/yourusername"
                InputProps={{
                  startAdornment: <GitHub sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Portfolio Website"
                name="profile.socialLinks.portfolio"
                value={formData.profile.socialLinks.portfolio}
                onChange={handleChange}
                placeholder="https://yourportfolio.com"
                InputProps={{
                  startAdornment: <Link sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }}>Documents</Divider>
        </Grid>

        {/* Document Upload Section in Edit Mode */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Description color="error" />
                <Box>
                  <Typography variant="subtitle1">
                    Resume <Chip label="Mandatory" size="small" color="error" />
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {uploadedDocuments.resume ? 
                      `Uploaded: ${uploadedDocuments.resume}` : 
                      'Upload your latest resume (PDF, DOC, DOCX) - Required'
                    }
                  </Typography>
                </Box>
              </Box>
              <>
                <input
                  type="file"
                  ref={resumeInputRef}
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileUpload(e, 'resume')}
                />
                <Button 
                  variant={uploadedDocuments.resume ? "outlined" : "contained"} 
                  startIcon={<Upload />}
                  color={uploadedDocuments.resume ? "success" : "primary"}
                  onClick={() => resumeInputRef.current?.click()}
                >
                  {uploadedDocuments.resume ? 'Change Resume' : 'Upload Resume'}
                </Button>
              </>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachFile color="primary" />
                <Box>
                  <Typography variant="subtitle1">
                    Cover Letter <Chip label="Optional" size="small" />
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {uploadedDocuments.coverLetter ? 
                      `Uploaded: ${uploadedDocuments.coverLetter}` : 
                      'Add a cover letter to enhance your application'
                    }
                  </Typography>
                </Box>
              </Box>
              <>
                <input
                  type="file"
                  ref={coverLetterInputRef}
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileUpload(e, 'coverLetter')}
                />
                <Button 
                  variant="outlined" 
                  startIcon={<Upload />}
                  onClick={() => coverLetterInputRef.current?.click()}
                >
                  {uploadedDocuments.coverLetter ? 'Change' : 'Upload'}
                </Button>
              </>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachFile color="primary" />
                <Box>
                  <Typography variant="subtitle1">
                    Certificates <Chip label="Optional" size="small" />
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {uploadedDocuments.certificates.length > 0 ? 
                      `Uploaded: ${uploadedDocuments.certificates.length} file(s)` : 
                      'Upload relevant certificates and credentials'
                    }
                  </Typography>
                </Box>
              </Box>
              <>
                <input
                  type="file"
                  ref={certificatesInputRef}
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileUpload(e, 'certificates')}
                />
                <Button 
                  variant="outlined" 
                  startIcon={<Upload />}
                  onClick={() => certificatesInputRef.current?.click()}
                >
                  Add Certificate
                </Button>
              </>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachFile color="primary" />
                <Box>
                  <Typography variant="subtitle1">
                    Work Portfolio Samples <Chip label="Optional" size="small" />
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {uploadedDocuments.portfolioSamples.length > 0 ? 
                      `Uploaded: ${uploadedDocuments.portfolioSamples.length} file(s)` : 
                      'Showcase your best work samples'
                    }
                  </Typography>
                </Box>
              </Box>
              <>
                <input
                  type="file"
                  ref={portfolioInputRef}
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileUpload(e, 'portfolio')}
                />
                <Button 
                  variant="outlined" 
                  startIcon={<Upload />}
                  onClick={() => portfolioInputRef.current?.click()}
                >
                  Add Sample
                </Button>
              </>
            </Box>
          </Paper>
        </Grid>

        {!uploadedDocuments.resume && (
          <Grid item xs={12}>
            <Alert severity="warning">
              Resume upload is mandatory to save your profile
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              sx={{ flex: 1 }}
            >
              {loading ? 'Updating...' : 'Save Profile'}
            </Button>
            {isProfileComplete() && (
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                onClick={handleCancel}
                startIcon={<Cancel />}
              >
                Cancel
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Container maxWidth="md">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          My Profile
        </Typography>
        {!isProfileComplete() && (
          <Chip 
            label="Profile Incomplete" 
            color="warning" 
            variant="outlined"
          />
        )}
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!isProfileComplete() && !editMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Please complete your profile including city and state to access all features and apply for jobs.
        </Alert>
      )}

      <Paper sx={{ mt: 3, p: 3 }}>
        {editMode ? <ProfileForm /> : <ProfileView />}
      </Paper>
    </Container>
  );
};

export default Profile;
